import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const CancellationRateOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, isLoading } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const cancelledByDate = new Map<string, number>();
        events.filter((e: any) => e.category === 'order' && (e.type || e.eventType) === 'cancelled').forEach((e: any) => {
            const date = e.date?.split('T')[0];
            if (date) cancelledByDate.set(date, (cancelledByDate.get(date) || 0) + 1);
        });

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        return sortedDates.map(dateStr => {
            const orders = finance.dailyBreakdown![dateStr].metrics.orders || 0;
            const cancelled = cancelledByDate.get(dateStr) || 0;
            const rate = orders > 0 ? (cancelled / orders) * 100 : 0;
            return { date: dateStr, dateLabel: format(parseISO(dateStr), 'MMM dd'), rate: Number(rate.toFixed(1)) };
        });
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="dateLabel" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis {...CHART_STYLES.axis} tickFormatter={(v) => `${v}%`} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={40} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => `${v}%`} />
                        <ReferenceLine y={5} stroke={CHART_COLORS.warning} strokeDasharray="3 3" label={{ value: '5%', position: 'right', fontSize: 10, fill: CHART_COLORS.warning }} />
                        <Line type="monotone" dataKey="rate" name={t('charts.cancellationRate', 'Cancellation Rate') as string} stroke={CHART_COLORS.danger} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
